import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ThumbsDownIcon from "@atlaskit/icon/core/thumbs-down";
import ThumbsUpIcon from "@atlaskit/icon/core/thumbs-up";

import { wacToken } from "@/libraries/design/foundations";

import { useFeedbackButtonsTracking } from "../hooks/tracking";

import { messages } from "./messages";

export const FeedbackButtons = () => {
    const intl = useIntl();
    const { fireFeedbackClickedEvent } = useFeedbackButtonsTracking();
    const [vote, setVote] = useState<"up" | "down" | null>(null);

    const hasVoted = vote !== null;

    const handleVote = (newVote: "up" | "down") => {
        if (hasVoted) {
            return;
        }
        setVote(newVote);
        fireFeedbackClickedEvent(newVote);
    };

    return (
        <div css={containerStyles} data-testid="feedback-buttons">
            <span css={labelStyles}>
                {intl.formatMessage(messages.feedbackLabel)}
            </span>
            <button
                css={[
                    iconButtonStyles,
                    vote === "up" && iconButtonActiveStyles
                ]}
                onClick={() => handleVote("up")}
                aria-label={intl.formatMessage(
                    messages.feedbackThumbsUpAriaLabel
                )}
                aria-pressed={vote === "up"}
                aria-disabled={hasVoted}
                disabled={hasVoted}
                type="button"
            >
                <ThumbsUpIcon label="" />
            </button>
            <button
                css={[
                    iconButtonStyles,
                    vote === "down" && iconButtonActiveStyles
                ]}
                onClick={() => handleVote("down")}
                aria-label={intl.formatMessage(
                    messages.feedbackThumbsDownAriaLabel
                )}
                aria-pressed={vote === "down"}
                aria-disabled={hasVoted}
                disabled={hasVoted}
                type="button"
            >
                <ThumbsDownIcon label="" />
            </button>
        </div>
    );
};

const containerStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px"
});

const labelStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.primary.black"),
    whiteSpace: "nowrap"
});

const iconButtonStyles = css({
    "display": "flex",
    "alignItems": "center",
    "justifyContent": "center",
    "background": "none",
    "border": "none",
    "padding": "0",
    "cursor": "pointer",
    "borderRadius": "4px",
    "color": wacToken("color.primary.black"),
    "transition": "opacity 0.15s ease",
    ":hover": {
        opacity: 0.7
    },
    ":focus-visible": {
        outline: `2px solid ${wacToken("color.primary.blue")}`,
        outlineOffset: "2px"
    },
    ":disabled": {
        "cursor": "initial",
        ":hover": {
            opacity: 1
        }
    }
});

const iconButtonActiveStyles = css({
    color: wacToken("color.primary.blue")
});
